<template>
    <div class="module-box" :style="customStyle">
        <el-row class="tac" style="height: 100%">
            <el-col :span="24" style="height: 100%">
                <div class="periodMenu">
                    <div style="width: 100%; display: flex; align-items: center; padding: 10px 5px; box-sizing: border-box">
                        <el-input
                            v-if="showFilter"
                            v-model="filterText"
                            placeholder="关键字"
                            style="margin: 0 10px; width: 80%"
                            clearable
                        />
                        <el-button type="primary" style="width: 20%" size="small" @click="filterTree">搜索</el-button>
                    </div>
                    <el-scrollbar :style="{ height: showFilter ? 'calc(100% - 50px)' : '100%' }">
                        <el-tree
                            :data="treeData"
                            :props="{
                                children: 'children',
                                label: 'name'
                            }"
                            :class="selectType"
                            ref="treeRef"
                            node-key="id"
                            :filter-node-method="filterNode"
                            :default-expanded-keys="expandedKey"
                            highlight-current
                            :expand-on-click-node="false"
                            @node-click="handleNodeClick"
                            :show-checkbox="showCheckbox"
                            @check="checkedData"
                            @current-change="handleCurrentChange"
                            @node-expand="handleNodeExpand"
                            v-loading="treeLoading"
                            v-bind="$attrs"
                        >
                            <template #default="{ node, data }">
                                <slot v-if="isSlot" v-bind:nodeData="{ node, data }"></slot>
                                <span v-else class="node-label" :title="data.name">{{ data.name }}</span>
                            </template>
                        </el-tree>
                    </el-scrollbar>
                </div>
            </el-col>
        </el-row>
    </div>
</template>

<script setup>
import { defineEmits, defineExpose, defineProps, nextTick, onMounted, ref, watch } from 'vue'
import { getDepartmentTree } from '@/api/dept.js'
import { getGradeTreeLists } from '@/api/periodApi/geriod.js'
import { getClassClassLists, getDeptTree } from '@/api/periodApi/class.js'
import { getSubjectTree } from '@/api/subject'
import { getRoomTreeLists } from '@/api/room'
import { getBuildingTreeOption } from '@/api/building.js'

const props = defineProps({
    type: {
        /**
         * dept 院系tree
         * grade 学段+年级tree
         * class 年级+班级tree
         * department 院系+班级
         * subject 院系+科目tree
         * teacher 院系+老师tree
         * room room tree
         * building 楼层 tree
         */
        type: String,
        required: true
    },
    extraParameter: {
        // 是否显示根节点
        type: Object,
        required: false,
        default: () => {
            return {}
        }
    },
    showCheckbox: {
        // 是否显示复选框
        type: Boolean,
        default: false
    },
    selectType: {
        // checkbox 多选  radio 单选
        type: String,
        default: () => {
            return 'checkbox'
        }
    },
    checkedList: {
        // 回显已选择的节点
        type: Array,
        required: false
    },
    showFilter: {
        // 是否显示过滤框
        type: Boolean,
        default: false
    },
    isHighlight: {
        // 是否默认选择节点
        type: Boolean,
        default: false
    },
    customStyle: {
        type: Object,
        default: {}
    },
    isSlot: {
        // 是否有插槽
        type: Boolean,
        default: false
    }
})
const emits = defineEmits(['change', 'updateForm', 'check', 'current-change'])

// tree列表数据
const treeData = ref([])
const filterText = ref('')
const treeRef = ref(null)

function clearChecked() {
    treeRef.value.setCheckedKeys([])
}
const filterTree = () => {
    treeRef.value.filter(filterText.value)
}
watch(
    () => props.type,
    (newVal) => {
        getTreeData()
    }
)
watch(
    () => props.checkedList,
    (newVal) => {
        if (newVal.length === 0) {
            // treeRef.value.setCheckedKeys([])
            clearChecked()
        }
    }
)
const filterNode = (value, data) => {
    if (!value) return true
    return data.name.includes(value)
}

const treeLoading = ref(false)
const expandedKey = ref([])
const handleNodeExpand = (data) => {
    expandedKey.value = []
}
const getTreeData = async () => {
    let child = []
    treeLoading.value = true
    if (props.type === 'dept') {
        // 院系列表
        treeData.value = await getDepartmentTree(props.extraParameter)
    } else if (props.type === 'grade') {
        // 学段年级班级
        child = await getGradeTreeLists()
        treeData.value = [
            {
                name: '全部',
                id: '',
                children: child
            }
        ]
    } else if (props.type === 'class') {
        child = await getClassClassLists(props.extraParameter)
        treeData.value = [
            {
                name: '全部',
                id: '',
                children: child
            }
        ]
    } else if (props.type === 'department') {
        treeData.value = await getDeptTree(props.extraParameter)
    } else if (props.type === 'subject') {
        treeData.value = await getSubjectTree(props.extraParameter)
    } else if (props.type === 'room') {
        treeData.value = await getRoomTreeLists(props.extraParameter)
    } else if (props.type === 'building') {
        treeData.value = await getBuildingTreeOption(props.extraParameter)
    }
    treeLoading.value = false
    nextTick(() => {
        if (props.checkedList?.length) {
            props.checkedList.forEach((ele) => {
                treeRef.value.setChecked(ele, true, false)
            })
        }
        if (props.isHighlight) {
            setTimeout(() => {
                selectFirstLeafNode()
            }, 10)
        }
        const firstLeafNode = getFirstLeafNode(treeData.value)
        if (firstLeafNode?.id) {
            expandedKey.value = [firstLeafNode.id]
        }
    })
}
const selectFirstLeafNode = () => {
    const firstLeafNode = getFirstLeafNode(treeData.value)
    if (firstLeafNode) {
        treeRef.value.setCheckedNodes([firstLeafNode], true)
        handleCurrentChange(firstLeafNode)
        handleNodeClick(firstLeafNode)
    }
}
const getFirstLeafNode = (nodes) => {
    for (const node of nodes) {
        if (!node.children || !node.children.length) {
            return node
        } else {
            const result = getFirstLeafNode(node.children)
            if (result) return result
        }
    }
    return null
}
// 选中菜单的回调
const handleNodeClick = (data) => {
    emits('change', data)
}
const checkedData = (data, node) => {
    let chooseData = []
    if (props.selectType === 'checkbox') {
        const arr = dealArray()
        arr.forEach((element) => {
            chooseData = chooseData.concat(element.children)
        })
    } else if (props.selectType === 'radio') {
        chooseData = [data]
        treeRef.value.setCheckedNodes([data])
    }
    if (props.showFilter && props.checkedList && props.checkedList.length) {
        const checkedIds = getLeafNodes(data.children)
        checkedIds.forEach((item) => {
            const node = treeRef.value.getNode(item)
            const inCheckedIds = checkedIds.filter((item) => item === node.key)
            if (!node.visible && node.checked && !inCheckedIds) {
                treeRef.value.setChecked(item, false)
            }
        })
        chooseData = treeRef.value.getCheckedNodes()
    }
    emits('check', data, node, chooseData)
}
function getLeafNodes(tree) {
    const arr = []
    tree &&
        tree.length &&
        tree.forEach((item) => {
            if (item.children && item.children.length) {
                arr.push(...this.getLeafNodes(item.children))
            } else {
                arr.push(item.id)
            }
        })
    return arr
}
const dealArray = () => {
    const checkedNodes = treeRef.value.getCheckedNodes(false, true)
    const checkedParam = []
    let index = -1
    if (checkedNodes.length !== 0) {
        checkedNodes.forEach((item) => {
            if (!item.children) {
                index++
                checkedParam[index] = {
                    label: item.label,
                    children: []
                }
                checkedParam[index].children.push(item)
            }
        })
    }
    return checkedParam
}

const handleCurrentChange = (data) => {
    emits('current-change', data)
}
/**
 * 被 keep-alive 缓存的组件激活时调用
 */
onMounted(() => {
    getTreeData()
})

defineExpose({
    getTreeData,
    treeData
})
</script>

<style lang="scss" scoped>
.radio {
    :deep(.el-icon.el-tree-node__expand-icon:not(.is-leaf) + .el-checkbox) {
        display: none !important;
    }
}
.module-box {
    min-width: 200px;
    width: 220px;
    height: 100%;
    background-color: inherit;
    border-right: 1px solid #d9d9e1;
    border-radius: 0 0 0 15px;
    .el-row {
        background-color: inherit;
        .el-col {
            background-color: inherit;
        }
    }
}

.periodMenu {
    height: 100%;
    background-color: inherit;
    .el-scrollbar {
        background-color: inherit;
    }
}

:deep(.el-menu) {
    border: 0px;
    background-color: #fafcfe;
}
.dot {
    position: absolute;
    right: 40px;
}
.allPeriod {
    width: 219px;
    padding-left: 20px;
    line-height: 50px;
    font-size: 14px;
    cursor: pointer;
    &:hover {
        background-color: #ecf5ff;
    }
}
.node-label {
    display: inline-block;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>
